import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useImportExport } from '@/hooks/useImportExport';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileDown,
  ArrowUpFromLine,
  ArrowDownToLine,
  FileText,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Stat pill component for displaying validation results
 */
const StatPill = ({ icon: Icon, label, value, color = "slate" }) => {
  const colorConfig = {
    slate: { bg: "bg-slate-50", icon: "bg-slate-100 text-slate-600", value: "text-slate-800" },
    emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-600", value: "text-emerald-700" },
    rose: { bg: "bg-rose-50", icon: "bg-rose-100 text-rose-600", value: "text-rose-700" },
    amber: { bg: "bg-amber-50", icon: "bg-amber-100 text-amber-600", value: "text-amber-700" },
    indigo: { bg: "bg-indigo-50", icon: "bg-indigo-100 text-indigo-600", value: "text-indigo-700" }
  };

  const colors = colorConfig[color] || colorConfig.slate;

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${colors.bg} flex-1 min-w-[140px]`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.icon}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex flex-col">
        <span className={`text-xl font-bold ${colors.value}`}>{value}</span>
        <span className="text-xs text-slate-500">{label}</span>
      </div>
    </div>
  );
};

/**
 * Validation record row component
 */
const ValidationRow = ({ record, type }) => {
  const getAccentColor = () => {
    switch (type) {
      case 'duplicate': return 'bg-indigo-500';
      case 'warning': return 'bg-amber-500';
      case 'invalid': return 'bg-rose-500';
      default: return 'bg-slate-300';
    }
  };

  const getBgColor = () => {
    switch (type) {
      case 'duplicate': return 'bg-indigo-50/50';
      case 'warning': return 'bg-amber-50/50';
      case 'invalid': return 'bg-rose-50/50';
      default: return 'bg-slate-50';
    }
  };

  const getBadgeStyle = () => {
    switch (type) {
      case 'duplicate': return 'bg-indigo-100 text-indigo-700 border-0';
      case 'warning': return 'bg-amber-100 text-amber-700 border-0';
      case 'invalid': return 'bg-rose-100 text-rose-700 border-0';
      default: return 'bg-slate-100 text-slate-700 border-0';
    }
  };

  return (
    <div className={`relative pl-4 py-3 pr-4 rounded-xl border border-slate-200 ${getBgColor()}`}>
      <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${getAccentColor()}`} />

      <div className="flex items-start gap-3">
        <Badge className={`rounded-full text-xs flex-shrink-0 ${getBadgeStyle()}`}>
          Row {record.rowNumber}
        </Badge>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800">
            {record.data?.email || '(no email)'} — {record.data?.leave_type || '(no type)'}
          </p>
          {type === 'duplicate' && (
            <p className="text-xs text-indigo-600 mt-1">
              {record.data?.original_start_date} to {record.data?.original_end_date} — Already exists
            </p>
          )}
          {type === 'warning' && record.warnings && (
            <ul className="text-xs text-amber-600 mt-1 space-y-0.5">
              {record.warnings.map((warning, idx) => (
                <li key={idx}>• {warning}</li>
              ))}
            </ul>
          )}
          {type === 'invalid' && record.errors && (
            <ul className="text-xs text-rose-600 mt-1 space-y-0.5">
              {record.errors.map((error, idx) => (
                <li key={idx}>• {error}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Template field item component
 */
const TemplateField = ({ name, description, required }) => (
  <div className="flex items-start gap-2">
    <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
    <div>
      <span className="text-sm font-medium text-slate-700">{name}</span>
      {required && <span className="text-rose-500 ml-1">*</span>}
      <span className="text-sm text-slate-500 ml-1">— {description}</span>
    </div>
  </div>
);

export const ImportExportPage = () => {
  const navigate = useNavigate();
  const { company, can, loading: authLoading, permissionsLoaded } = useAuth();

  usePageTitle('Import/Export Leave Data');

  // Auth is ready when not loading AND permissions have been loaded
  const authReady = !authLoading && permissionsLoaded;
  const canManageAttendance = can('attendance.manage');

  // Redirect non-authorized users (only after auth has loaded)
  useEffect(() => {
    if (authReady && !canManageAttendance) {
      toast.error('You do not have permission to import/export leave data');
      navigate('/dashboard');
    }
  }, [authReady, canManageAttendance, navigate]);

  // Use optimized import/export hook
  const {
    downloadTemplate,
    validateImport,
    executeImport,
    exportData,
    resetValidation,
    validationResults,
    importProgress,
    loading,
    exporting,
    fileInputRef
  } = useImportExport(company?.id);

  /**
   * Handle template download
   */
  const handleDownloadTemplate = useCallback(() => {
    downloadTemplate();
    toast.success('Template CSV downloaded');
  }, [downloadTemplate]);

  /**
   * Handle file upload and validation
   */
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    try {
      const text = await file.text();
      const result = await validateImport(text);

      if (result.success) {
        toast.success(`Parsed ${result.rowCount} rows from CSV`);
      } else {
        toast.error(`Failed to parse CSV: ${result.error}`);
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      toast.error(`Failed to parse CSV: ${error.message}`);
    }
  }, [validateImport]);

  /**
   * Handle import execution
   */
  const handleImport = useCallback(async () => {
    const result = await executeImport();

    if (result.success) {
      const messages = [];
      if (result.inserted > 0) {
        messages.push(`${result.inserted} imported`);
      }
      if (result.skipped > 0) {
        messages.push(`${result.skipped} skipped (duplicates)`);
      }

      if (result.inserted > 0) {
        toast.success(messages.join(', '));
      } else {
        toast.info(messages.join(', '));
      }

      if (result.errors?.length > 0) {
        toast.error(`${result.errors.length} records failed`);
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      toast.error(result.error || 'Failed to import data');
    }
  }, [executeImport, fileInputRef]);

  /**
   * Handle export
   */
  const handleExport = useCallback(async () => {
    const result = await exportData();

    if (result.success) {
      toast.success(`Exported ${result.count} records`);
    } else {
      toast.error(result.error || 'Failed to export data');
    }
  }, [exportData]);

  /**
   * Handle cancel/reset
   */
  const handleCancel = useCallback(() => {
    resetValidation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetValidation, fileInputRef]);

  // Show loading while auth is being verified
  if (!authReady) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }

  // Don't render anything if user doesn't have permission (redirect will happen via useEffect)
  if (!canManageAttendance) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="space-y-6 max-w-4xl"
    >
      {/* Hero Section */}
      <div className="rounded-2xl bg-white border border-slate-200 p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
            <FileSpreadsheet className="w-6 h-6 text-slate-700" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 tracking-tight">
              Import/Export Leave Data
            </h1>
            <p className="text-slate-500 mt-1">
              Import historic leave data in bulk or export existing records
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-slate-100">
          <Button
            onClick={handleDownloadTemplate}
            variant="outline"
            className="rounded-full px-5"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            variant="outline"
            className="rounded-full px-5"
          >
            {exporting ? (
              <>
                <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4 mr-2" />
                Export All Records
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Template Format Card */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
            <FileText className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">CSV Template Format</h2>
            <p className="text-sm text-slate-500">Required and optional columns for import</p>
          </div>
        </div>

        <div className="space-y-2 pl-1">
          <TemplateField name="email" description="User's email address" required />
          <TemplateField name="leave_type" description="Type of leave (Annual Leave, Sick Leave, etc.)" required />
          <TemplateField name="start_date" description="DD-MM-YYYY, DD/MM/YYYY, or DD.MM.YYYY" required />
          <TemplateField name="end_date" description="DD-MM-YYYY, DD/MM/YYYY, or DD.MM.YYYY" required />
          <TemplateField name="start_period" description="full, am, or pm (default: full)" />
          <TemplateField name="end_period" description="full, am, or pm (default: full)" />
          <TemplateField name="calculated_days" description="Number of days (optional)" />
          <TemplateField name="user_note" description="Optional note/reason" />
          <TemplateField name="status" description="approved, pending, or rejected (default: approved)" />
        </div>

        <div className="flex items-start gap-3 mt-5 p-4 rounded-xl bg-slate-50 border border-slate-200">
          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Info className="h-4 w-4 text-slate-600" />
          </div>
          <p className="text-sm text-slate-600">
            Download the template above to get started. Fill in your historic leave data and upload it below for validation.
          </p>
        </div>
      </div>

      {/* Import Section */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <ArrowUpFromLine className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Import Leave Data</h2>
            <p className="text-sm text-slate-500">Upload a CSV file with historic leave records</p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="csv-upload" className="text-sm text-slate-600">Select CSV File</Label>
            <Input
              id="csv-upload"
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={loading}
              className="rounded-full file:rounded-full file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium file:mr-3 hover:file:bg-slate-200"
            />
          </div>

          {/* Validation Results */}
          {validationResults && (
            <div className="space-y-5 pt-4 border-t border-slate-100">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatPill
                  icon={CheckCircle}
                  label="Valid Records"
                  value={validationResults.valid.length}
                  color="emerald"
                />
                <StatPill
                  icon={XCircle}
                  label="Invalid Records"
                  value={validationResults.invalid.length}
                  color="rose"
                />
                <StatPill
                  icon={AlertCircle}
                  label="Warnings"
                  value={validationResults.warnings.length}
                  color="amber"
                />
                <StatPill
                  icon={FileSpreadsheet}
                  label="Duplicates (skip)"
                  value={validationResults.duplicates.length}
                  color="indigo"
                />
              </div>

              {/* Duplicates */}
              {validationResults.duplicates.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-indigo-800">
                    Duplicate Records (will be skipped)
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {validationResults.duplicates.slice(0, 10).map((record) => (
                      <ValidationRow key={record.rowNumber} record={record} type="duplicate" />
                    ))}
                    {validationResults.duplicates.length > 10 && (
                      <p className="text-sm text-center text-slate-500 py-2">
                        ... and {validationResults.duplicates.length - 10} more duplicate records
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {validationResults.warnings.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-amber-800">
                    Records with Warnings
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {validationResults.warnings.slice(0, 10).map((record) => (
                      <ValidationRow key={record.rowNumber} record={record} type="warning" />
                    ))}
                    {validationResults.warnings.length > 10 && (
                      <p className="text-sm text-center text-slate-500 py-2">
                        ... and {validationResults.warnings.length - 10} more records with warnings
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Invalid Records */}
              {validationResults.invalid.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-rose-800">
                    Invalid Records
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {validationResults.invalid.slice(0, 10).map((record) => (
                      <ValidationRow key={record.rowNumber} record={record} type="invalid" />
                    ))}
                    {validationResults.invalid.length > 10 && (
                      <p className="text-sm text-center text-slate-500 py-2">
                        ... and {validationResults.invalid.length - 10} more invalid records
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-100">
                <Button
                  onClick={handleImport}
                  disabled={loading || validationResults.valid.length === 0}
                  className="flex-1 rounded-full bg-slate-800 hover:bg-slate-700"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      {importProgress
                        ? `Importing ${importProgress.current}/${importProgress.total}...`
                        : 'Importing...'}
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Import {validationResults.valid.length - validationResults.duplicates.length} New Records
                      {validationResults.duplicates.length > 0 &&
                        ` (skip ${validationResults.duplicates.length})`}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading}
                  className="rounded-full px-6"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Export Section */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <ArrowDownToLine className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Export Existing Data</h2>
              <p className="text-sm text-slate-500">Download all leave records as a CSV file</p>
            </div>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting}
            variant="outline"
            className="rounded-full px-5"
          >
            {exporting ? (
              <>
                <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4 mr-2" />
                Export All Records
              </>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
